/**
 * CancelSyncButton — inline "Annulla sync" trigger (V1 audit G8).
 *
 * Mounts next to the ``SyncStatusPill`` on a connection row when a job
 * is actively running for that connection. Clicking it calls
 * ``useDriveSyncCancel`` with the live ``jobId`` from the unified
 * ``SyncStore``; the pill flips to "Annullando…" automatically because
 * the store optimistically marks the job ``cancelling`` before the BE
 * confirms.
 *
 * V1 reference: ``ando-frontend/app/components/drive/ProviderCard.tsx``
 * lines 380-390 (red destructive button inside the sync-progress box).
 * V2 surface is inline + compact — same intent, half the chrome.
 */

import { StopCircle } from "lucide-react";
import type * as React from "react";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";

import { cn, toast } from "@ando/ui";

import { useDriveSyncCancel } from "@/features/sync/hooks/useDriveSync";
import {
  type SyncIdentifier,
  type SyncSource,
  isJobActive,
  useLatestJobFor,
} from "@/features/sync/state/syncStore";

export interface CancelSyncButtonProps {
  readonly source: SyncSource;
  readonly match: (id: SyncIdentifier) => boolean;
  readonly className?: string;
}

export const CancelSyncButton: React.FC<CancelSyncButtonProps> = ({
  source,
  match,
  className,
}) => {
  const job = useLatestJobFor(source, match);
  const cancel = useDriveSyncCancel();
  const { i18n } = useLingui();

  if (!job) return null;
  // Only surface during truly-active phases. ``cancelling`` already shows
  // the pill in grey; firing the button again would double-call the BE
  // and is disabled. Terminal statuses bail at the pill level (the row
  // toast owns the surface). Shared predicate in ``syncStore`` so the
  // pill and the button NEVER drift on what "active" means.
  if (!isJobActive(job.status)) return null;

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    cancel.mutate(job.jobId, {
      onSuccess: () => {
        toast.success(i18n._(msg`Sincronizzazione annullata`));
      },
      onError: (err) => {
        toast.error(
          err instanceof Error
            ? i18n._(msg`Impossibile annullare: ${err.message}`)
            : i18n._(msg`Impossibile annullare la sincronizzazione.`),
        );
      },
    });
  };

  const cancelLabel = i18n._(msg`Annulla sincronizzazione`);
  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={cancel.isPending}
      aria-label={cancelLabel}
      title={cancelLabel}
      className={cn(
        "inline-flex h-6 w-6 items-center justify-center rounded-md",
        "text-ink-3 hover:bg-error/10 hover:text-error",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-error/45",
        "disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
    >
      <StopCircle className="h-3.5 w-3.5" strokeWidth={2} aria-hidden />
    </button>
  );
};

CancelSyncButton.displayName = "CancelSyncButton";
